/**
 * Admin API Module
 *
 * Handles admin operations:
 * - Dashboard stats
 * - Portal-user management (list/edit/modules/trial/subscription/impersonate)
 * - Email log viewer
 * - Trial invitations + telemetry export
 *
 * Customer / Service-Integrator / SI-themes / legacy-enterprise-user methods
 * were removed when the underlying tables (customers, service_integrators)
 * were retired during the org-unification migration. Org-level admin lives
 * at /admin/orgs now.
 *
 * The /api/direct-requests/* group went the same way: the backend never
 * mounted that prefix after the migration, so every one of those calls
 * 404'd. Enterprise intake runs through /admin/revenue now.
 */

import { http } from './base';

export const adminApi = {

  // ===== DASHBOARD STATS =====

  /**
   * The six numbers the admin dashboard shows, each with the filtered list it
   * links to. Marketing may read this, but gets `mrr: null` — see
   * `CockpitResponse`.
   */
  async getCockpit(): Promise<CockpitResponse> {
    const { data } = await http.get<CockpitResponse>('/api/admin/cockpit');
    return data;
  },

  // `getDashboardStats()` is gone with the endpoint behind it. It counted
  // nothing anyone acted on — `totalApiCalls` measured the admin panel's own
  // page loads, `totalServiceIntegrators` read `plan_type = 'enterprise'` on an
  // `enum('individual','team')` column and was structurally 0 — and leaving
  // the client method would have left a typed call at a route that now 404s.
  // `getCockpit()` replaces it.

  // ===== USERS =====

  async getPortalUsers(filters?: {
    search?: string;
    planType?: 'individual' | 'team' | 'enterprise';
    sortBy?: 'created_at' | 'last_login_at' | 'login_count';
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<{ users: any[]; count: number }> {
    // Endpoint moved from the deleted /api/user-auth/admin/* group to
    // /api/admin/users (single source for all portal users now that
    // customers/service_integrators tables are gone).
    const { data } = await http.get<{ users: any[]; count: number; success: boolean }>(
      '/api/admin/users',
      { params: filters }
    );
    return { users: data.users, count: data.count };
  },

  /**
   * PARTIAL update of a portal user: only the keys present are written, so an
   * omitted field keeps its stored value. `role` is the PLATFORM role
   * (portal_users.role) and is admin-only server-side; `status` is the org
   * MEMBERSHIP status.
   *
   * Send `organizationId` whenever `status` is sent. It scopes the write to
   * the membership in THAT organisation — the account panel edits a member of
   * one specific org, and without it the backend falls back to the user's
   * primary membership, which for someone in two orgs is not the row on
   * screen. It also makes re-activating a removed member work at all: the
   * fallback lookup only sees active memberships.
   */
  async updatePortalUser(userId: number, updates: {
    name?: string;
    email?: string;
    status?: 'active' | 'pending' | 'removed';
    organizationId?: number;
    role?: 'user' | 'marketing';
    emailVerified?: boolean;
    onboardingCompleted?: boolean;
  }): Promise<any> {
    const { data } = await http.put<{ user: any; success: boolean }>(
      `/api/admin/users/${userId}`,
      updates
    );
    return data.user;
  },

  async deletePortalUser(userId: number): Promise<void> {
    await http.delete(`/api/admin/users/${userId}`);
  },

  async impersonateUser(userId: number): Promise<{ token: string; user: any; organization: any; subscription: any }> {
    const { data } = await http.post(`/api/admin/users/${userId}/impersonate`);
    return data;
  },

  /**
   * @deprecated Sends every field unconditionally, so a caller that seeds its
   * price fields at 0 hits the endpoint's zero-amount guard and 400s (and
   * before that guard existed, silently nulled the price). Use
   * `updateAccountSubscription`, which sends only what changed. Kept only
   * because api/client.ts re-exports it.
   */
  async updatePortalUserSubscription(userId: number, updates: {
    status?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'free';
    totalAmountCents?: number;
    pricePerSeatCents?: number;
    cancelAtPeriodEnd?: boolean;
    trialEndsAt?: number | null;
  }): Promise<{ subscription: any; message: string }> {
    const { data } = await http.put<{ success: boolean; subscription: any; message: string }>(
      `/api/admin/users/${userId}/subscription`,
      updates
    );
    return { subscription: data.subscription, message: data.message };
  },

  async getPortalUserModules(userId: number): Promise<string[]> {
    const { data } = await http.get<{ success: boolean; modules: string[] }>(
      `/api/admin/users/${userId}/modules`
    );
    return data.modules;
  },

  /**
   * @deprecated Always 400s. The endpoint rejects a bare `{ modules }` as
   * ambiguous — it cannot tell "these are the only modules" from "add these" —
   * and the full-replace it used to mean would cancel everything the customer
   * had whenever the caller's list had not loaded yet. Use
   * `updateAccountModules` (add/remove delta) instead. Kept only because
   * api/client.ts re-exports it.
   */
  async setPortalUserModules(userId: number, modules: string[]): Promise<{ modules: string[]; message: string }> {
    const { data } = await http.put<{ success: boolean; modules: string[]; message: string }>(
      `/api/admin/users/${userId}/modules`,
      { modules }
    );
    return { modules: data.modules, message: data.message };
  },

  async updatePortalUserTrial(userId: number, options: {
    action: 'extend' | 'expire' | 'set';
    days?: number;
  } | {
    trialEndsAt: number | null;
  }): Promise<{ success: boolean; trialEndsAt: number | null; daysRemaining: number | null; message: string }> {
    const { data } = await http.put<{ success: boolean; trialEndsAt: number | null; daysRemaining: number | null; message: string }>(
      `/api/admin/users/${userId}/trial`,
      options
    );
    return data;
  },


  // ===== ACCOUNTS (org-level admin — replaces /admin/users + /admin/orgs) =====

  /**
   * One row per ORGANISATION (the tenant), with the behavioural columns the
   * per-user and per-org lists never showed. Unknown filter values are
   * ignored by the backend ("no filter"), never treated as an empty result.
   */
  async getAccounts(filters?: AccountFilters): Promise<AccountsResponse> {
    const { data } = await http.get<AccountsResponse>('/api/admin/accounts', {
      params: filters,
    });
    return data;
  },

  /**
   * The same account object plus members / active features / instances /
   * recent (redacted) chat errors. Tolerates both a flat body and one
   * wrapped in `account`, so a backend that nests it stays compatible.
   */
  async getAccountDetail(organizationId: number): Promise<AccountDetail> {
    const { data } = await http.get<{ success: boolean; account?: AccountDetail } & AccountDetail>(
      `/api/admin/accounts/${organizationId}`
    );
    return (data.account ?? data) as AccountDetail;
  },

  /**
   * PARTIAL subscription update: pass ONLY the fields the admin actually
   * changed. The old per-user editor started its state at 0 and sent every
   * field unconditionally, which zeroed the price of any subscription it
   * touched. Callers must not fill in "current" values here.
   */
  async updateAccountSubscription(
    ownerUserId: number,
    patch: AccountSubscriptionPatch
  ): Promise<{ subscription: any; message: string }> {
    const { data } = await http.put<{ success: boolean; subscription: any; message: string }>(
      `/api/admin/users/${ownerUserId}/subscription`,
      patch
    );
    return { subscription: data.subscription, message: data.message };
  },

  /**
   * Deletes the whole tenant behind this account. The backend cancels the
   * Mollie subscription FIRST and refuses (502 + `requiresForce`) if that
   * cancellation fails, so a deleted tenant can't keep being charged.
   * `force` overrides that refusal — only after the admin has seen why.
   */
  async deleteAccount(ownerUserId: number, options?: { force?: boolean }): Promise<void> {
    await http.delete(`/api/admin/users/${ownerUserId}`, {
      params: options?.force ? { force: 'true' } : undefined,
    });
  },

  /**
   * Start / extend / expire the trial. `set` works for accounts that have no
   * subscription row yet — it provisions one instead of 404-ing.
   */
  async updateAccountTrial(
    ownerUserId: number,
    options: { action: 'extend' | 'expire' | 'set'; days?: number }
  ): Promise<{ success: boolean; trialEndsAt: number | null; daysRemaining: number | null; message: string }> {
    const { data } = await http.put<{
      success: boolean;
      trialEndsAt: number | null;
      daysRemaining: number | null;
      message: string;
    }>(`/api/admin/users/${ownerUserId}/trial`, options);
    return data;
  },

  async getAccountModules(ownerUserId: number): Promise<string[]> {
    const { data } = await http.get<{ success: boolean; modules: string[] }>(
      `/api/admin/users/${ownerUserId}/modules`
    );
    return data.modules;
  },

  /**
   * Explicit add/remove delta instead of a full replace. The full-replace
   * variant combined with an empty initial state meant "save" could strip
   * every module an account had.
   */
  async updateAccountModules(
    ownerUserId: number,
    changes: { add: string[]; remove: string[] }
  ): Promise<{ modules: string[]; message: string }> {
    const { data } = await http.put<{ success: boolean; modules: string[]; message: string }>(
      `/api/admin/users/${ownerUserId}/modules`,
      changes
    );
    return { modules: data.modules, message: data.message };
  },

  async elevateAccountToEnterprise(organizationId: number): Promise<void> {
    await http.post(`/api/admin/orgs/${organizationId}/elevate-to-enterprise`);
  },

  async demoteAccountFromEnterprise(organizationId: number): Promise<void> {
    await http.post(`/api/admin/orgs/${organizationId}/demote-from-enterprise`);
  },

  // ===== EMAIL LOGS =====

  async getEmailLogs(filters?: {
    search?: string;
    type?: string;
    status?: 'sent' | 'failed';
    from?: number;
    to?: number;
    limit?: number;
    offset?: number;
  }): Promise<EmailLogsResponse> {
    const { data } = await http.get<EmailLogsResponse>(
      '/api/admin/email-logs',
      { params: filters }
    );
    return data;
  },

  async getEmailLogDetail(id: number): Promise<EmailLogItem> {
    const { data } = await http.get<{ success: boolean; log: EmailLogItem }>(
      `/api/admin/email-logs/${id}`
    );
    return data.log;
  },

  // ===== TRIAL INVITATIONS =====

  async sendTrialMagicLink(data: {
    email: string;
    trialMonths: number;
    planType: string;
    note?: string;
    language?: string;
  }): Promise<{ success: boolean; isExistingUser: boolean }> {
    const { data: result } = await http.post<{ success: boolean; isExistingUser: boolean }>(
      '/api/admin/trial-invitations/magic-link',
      data
    );
    return result;
  },

  // ===== ANONYMOUS TELEMETRY =====

  async getAnonymousTelemetryStats(days = 30): Promise<AnonymousTelemetryStats> {
    const { data } = await http.get<{ success: boolean; data: AnonymousTelemetryStats }>(
      `/api/admin/telemetry/stats?days=${days}`
    );
    return data.data;
  },

  async getErrorTelemetrySessions(days = 30, limit = 50): Promise<ErrorTelemetrySessions> {
    const { data } = await http.get<{ success: boolean; data: ErrorTelemetrySessions }>(
      `/api/admin/telemetry/error-sessions?days=${days}&limit=${limit}`
    );
    return data.data;
  },

  // ===== AI CRAWLER ANALYTICS =====

  async getAiCrawlerStats(days = 30, host?: string): Promise<AiCrawlerStats> {
    const params = new URLSearchParams({ days: String(days) });
    if (host) params.set('host', host);
    // This endpoint returns its fields at the top level (not nested under
    // `data`), so we hand back the whole response.
    const { data } = await http.get<AiCrawlerStats & { success: boolean }>(
      `/api/admin/analytics/ai-crawlers?${params.toString()}`
    );
    return data;
  },

  async runAiCrawlerSync(): Promise<AiCrawlerSyncResult> {
    const { data } = await http.post<{ success: boolean; result: AiCrawlerSyncResult }>(
      `/api/admin/analytics/ai-crawlers/sync`
    );
    return data.result;
  },

  // ===== REPORTING — ACTIVATION FUNNEL =====

  /**
   * The eleven-step signup→paid funnel, the exclusive "furthest step reached"
   * split, and the two time-to-value distributions. REPLACES the old
   * `reportingApi.getActivation()` at the same path: that response reported one
   * scalar activation rate over portal USERS, which is a different population
   * from the one every other activation surface (cockpit, /admin/accounts) is
   * counted in. One endpoint, one population — organisations.
   */
  async getActivationFunnel(filters?: ActivationFunnelFilters): Promise<ActivationFunnelData> {
    const { data } = await http.get<ActivationFunnelData>('/api/admin/reporting/activation', {
      params: filters,
    });
    return data;
  },

  // ===== OPS (/admin/ops) =====
  //
  // The page you open when something is broken. Every call below is its own
  // block on that page: one failing request must leave the other four
  // readable, so nothing here is bundled into a single "give me everything"
  // endpoint whose first failed query blanks the screen.

  /**
   * The error register (`error_reports`), which nothing has ever listed.
   *
   * "Open" is `openErrorReportSql()` in the backend — new + reporting +
   * reported — NOT a time window: an error last seen in June is still open.
   * Muting and resolving are the two statuses that mean somebody dealt with
   * it, and they are what `updateOpsErrorStatus` writes.
   *
   * This is deliberately NOT `api_logs`. That table holds `/api/admin/*`
   * traffic and nothing else, so it cannot see the customer-facing 502 on
   * /scan-instance or the 500 on /providers that this register does.
   */
  async getOpsErrors(filters?: { status?: OpsErrorFilter; limit?: number; days?: number }): Promise<OpsErrorsResponse> {
    const { data } = await http.get<OpsErrorsResponse>('/api/monitoring/ops/errors', {
      params: filters,
    });
    return data;
  },

  /**
   * Mute or resolve one fingerprint, or put it back on the list (`new`).
   * The three writable values are the ones the enum already carries meaning
   * for; `reporting`/`reported` belong to the issue reporter and are not
   * settable by hand.
   */
  async updateOpsErrorStatus(
    id: number,
    status: OpsErrorWritableStatus
  ): Promise<OpsErrorUpdateResult> {
    const { data } = await http.patch<OpsErrorUpdateResult>(
      `/api/monitoring/ops/errors/${id}`,
      { status }
    );
    return data;
  },

  /**
   * Recent 5xx from `api_logs`.
   *
   * ⚠️ `api_logs.license_key` MUST NOT travel in this response and MUST NOT
   * be rendered. Until wave 0 that column held the admin key in plain text —
   * 169 rows of it on production. `OpsServerError` has no field for it on
   * purpose: if the backend ever adds one, the type stops it here rather than
   * a reviewer catching it in JSX.
   *
   * `scope` says what this table actually covers, because "recent server
   * errors" reads as "everything" and it is one mount's traffic.
   */
  async getOpsServerErrors(filters?: { days?: number; limit?: number }): Promise<OpsServerErrorsResponse> {
    const { data } = await http.get<OpsServerErrorsResponse>('/api/monitoring/ops/server-errors', {
      params: filters,
    });
    return data;
  },

  /**
   * Mail health from `email_logs` + the `delivery_state` columns migration 146
   * added.
   *
   * The three buckets are NOT a status breakdown: `noVerdict` is the absence
   * of an answer, not a delivery. Every row before the Resend cutover
   * (`verdictSince`) was sent over SMTP, which reported success whether or not
   * the mail existed on the other end — which is why `failed` counted on its
   * own would read as "nothing goes wrong".
   */
  async getOpsMailHealth(filters?: { days?: number }): Promise<OpsMailHealth> {
    const { data } = await http.get<OpsMailHealth>('/api/monitoring/ops/mail-health', {
      params: filters,
    });
    return data;
  },

  /**
   * The health overview /admin/monitoring showed, from the same endpoint —
   * repaired in wave 4b, not rebuilt.
   *
   * Retyped here rather than reused from `api/customer.ts`'s `HealthStatus`
   * because the repair adds fields that type does not describe: a real
   * `version` (it was pinned at `1.0.0` by an env var that does not exist
   * under `node dist/index.js`), and a check status that means "this measures
   * nothing" so it can be kept out of "N of M healthy".
   */
  async getOpsHealth(): Promise<OpsHealth> {
    const { data } = await http.get<OpsHealth>('/api/monitoring/health/detailed');
    return data;
  },

  // ===== REVENUE (/admin/revenue) =====
  //
  // Replaces the /api/admin/direct-billing/* surface, which the frontend
  // called thirty times and the backend never mounted: `api/billing.ts`
  // renamed every url from `enterprise-billing` to `direct-billing` during the
  // April branding sweep, and the only route that ever existed under that
  // prefix was `/direct-billing/dashboard` — reading three tables migration
  // 114 dropped, behind a swallowed catch, so it answered 200 with zeroes.
  //
  // ONE endpoint, not five. The page reads `subscriptions` (20 rows),
  // `invoices` (1), `payments` (12), `platform_settings` (1) and the portal
  // side of the Mollie picture from a single `GET /api/admin/revenue`, which
  // runs them in one `Promise.all` server-side. Splitting the client across
  // urls the server does not mount is precisely how the three pages this
  // replaces came to be dead, so there is exactly one url here and it is
  // mounted.

  /**
   * Everything on /admin/revenue except the Enterprise inbox and the company
   * card's write.
   *
   * MRR IS NOT RECOMPUTED IN THE FRONTEND. `totals.mrrCents` comes from the
   * backend's `MRR_CENTS_SQL` over `status = 'active'` on the latest row per
   * organisation — the same expression `/admin` and `/admin/reporting` read.
   * Summing `mrrCents` over `subscriptions` instead would produce a very
   * different, very wrong number: fourteen of the twenty rows on production
   * carry `price_per_seat_cents = 1900` while sitting in `free`, `canceled` or
   * `trialing`, so a naive sum reports €266,00 where Mollie charges €19,00
   * (counted on production 2026-08-17). `countsTowardMrr` marks the rows the
   * total is actually made of, and the page renders an amount on those only.
   *
   * `totals.internalMrrCents` is not decoration. All of today's MRR is
   * organisation 56, our own dogfood account, and a screen that prints €19,00
   * without saying so reads as "we have a customer".
   *
   * There is NO zero-filled fallback behind this call. A failure is a 500 and
   * the page renders an error, because a silent zero on a revenue screen is
   * the exact bug this wave exists to remove.
   */
  async getRevenueOverview(): Promise<RevenueOverviewResponse> {
    const { data } = await http.get<RevenueOverviewResponse>('/api/admin/revenue');
    return data;
  },

  /**
   * Enterprise intake (`upgrade_requests`), the one inbound path still alive —
   * `request_type = 'org_enterprise_plan'`, migration 140. Kept on its own
   * endpoint rather than read from the overview because the filters are
   * applied server-side and the list is refetched after a review.
   *
   * The other five `request_type` values are seat/slot requests from the
   * customer and service-integrator intake, whose `authenticateCustomer` /
   * `authenticateSI` endpoints answered 401 from April until wave 5 deleted
   * them. Rows of that kind can no longer be created; old ones would still
   * list, so the filter keeps them addressable.
   */
  async getRevenueUpgradeRequests(filters?: {
    status?: RevenueUpgradeRequestStatus | 'all';
    type?: RevenueUpgradeRequestType | 'all';
  }): Promise<RevenueUpgradeRequest[]> {
    const { data } = await http.get<{ requests: RevenueUpgradeRequest[] }>(
      '/api/upgrade-requests/admin',
      {
        params: {
          status: filters?.status === 'all' ? undefined : filters?.status,
          type: filters?.type === 'all' ? undefined : filters?.type,
        },
      },
    );
    return data.requests ?? [];
  },

  async getRevenueUpgradeRequestStats(): Promise<RevenueUpgradeRequestStats> {
    const { data } = await http.get<{ stats: RevenueUpgradeRequestStats }>(
      '/api/upgrade-requests/admin/stats',
    );
    return data.stats;
  },

  /**
   * Approve or reject one request. Approving is a sales signal, not a billing
   * action: it stamps the row with your notes and mails the requester, and
   * writes nothing to the account's tier, seats or price. Moving an org to
   * Enterprise is a separate, explicit act — the "Elevate" button on
   * /admin/accounts.
   */
  async reviewRevenueUpgradeRequest(
    requestId: number,
    action: 'approve' | 'reject',
    reviewNotes?: string,
  ): Promise<{ success: boolean }> {
    const { data } = await http.post<{ success: boolean }>(
      `/api/upgrade-requests/admin/review/${requestId}`,
      { action, review_notes: reviewNotes },
    );
    return data;
  },

  /**
   * The issuer identity printed on every generated invoice PDF.
   *
   * This is the one write on the Revenue page that is genuinely urgent.
   * `platform_settings` has a single row, still holding migration 137's
   * placeholders (KvK `12345678`, VAT `NL123456789B01`,
   * IBAN `NL00 ABNA 0000 0000 00`) with `updated_at = 0` on production, and
   * `routes/billing.ts` + `routes/customer.ts` render it into every PDF a
   * customer downloads. Admin-only server-side, like the page.
   *
   * `placeholderFields` names the fields still equal to that seed, computed
   * from the same constants the revenue payload uses, so the warning cannot
   * drift out of step with a second copy of the literals in this file.
   */
  async getPlatformSettings(): Promise<PlatformSettingsWithFlags> {
    const { data } = await http.get<PlatformSettingsWithFlags>('/api/admin/platform-settings');
    return data;
  },

  /** PUT answers with the stored row, so the caller never has to re-read. */
  async updatePlatformSettings(fields: PlatformSettingsPatch): Promise<PlatformSettingsWithFlags> {
    const { data } = await http.put<PlatformSettingsWithFlags>(
      '/api/admin/platform-settings',
      fields,
    );
    return data;
  },
};

// ===== OPS =====

/** The five values of `error_reports.status`. */
export type OpsErrorStatus = 'new' | 'reporting' | 'reported' | 'muted' | 'resolved';

/**
 * What an admin may set by hand. `reporting` and `reported` are the issue
 * reporter's own states — it claims a row before calling GitHub and stamps the
 * url afterwards — so setting them from a button would lie about an issue that
 * does not exist.
 */
export type OpsErrorWritableStatus = 'muted' | 'resolved' | 'new';

/** `open` = new + reporting + reported. Default when the caller sends nothing. */
export type OpsErrorFilter = 'open' | 'muted' | 'resolved' | 'all';

/**
 * What the PATCH answers. Not the updated row: the write is a status change on
 * a row this page already has, and `outcome` carries the one thing the caller
 * cannot infer — `unchanged` means the row was already in that state, which is
 * a successful no-op rather than a second mute.
 *
 * A 409 (the reporter holds a GitHub claim on this row) and a 404 arrive as
 * thrown errors, not as an outcome — they are not successes.
 */
export interface OpsErrorUpdateResult {
  success: boolean;
  id: number;
  status: OpsErrorWritableStatus;
  outcome: 'updated' | 'unchanged';
}

export interface OpsErrorReport {
  id: number;
  /** sha256 dedup key. Shown truncated — two rows can carry the same title. */
  fingerprint: string;
  /** Scrubbed first line of the error. Never a payload, never a stack. */
  title: string;
  source: 'http' | 'uncaughtException' | 'unhandledRejection';
  /** Occurrences of this fingerprint, all-time. */
  count: number;
  /** Epoch ms. */
  firstSeen: number;
  /** Epoch ms — the one that says whether this is still happening. */
  lastSeen: number;
  status: OpsErrorStatus;
  /** `null` until an issue was actually opened. On production: always. */
  githubIssueUrl: string | null;
}

/**
 * Health of the error→GitHub-issue reporter itself.
 *
 * Optional so a backend that predates it degrades to "derived from the rows"
 * instead of crashing the block — but it is what lets the page tell "nothing
 * was worth reporting" apart from "the reporting is broken". On production
 * every one of the sixteen rows sits at status `new` with a NULL url, which is
 * the second of those two and looked exactly like the first.
 */
export interface OpsErrorReporting {
  /** `ERROR_TO_ISSUE_ENABLED`. */
  enabled: boolean;
  /** `ERROR_TO_ISSUE_THRESHOLD` — occurrences before an issue is opened. */
  threshold: number | null;
  /** Rows that carry a `github_issue_url`. */
  linked: number;
  /** Open rows that do not. */
  unlinked: number;
}

export interface OpsErrorsResponse {
  success: boolean;
  errors: OpsErrorReport[];
  /** Rows matching the filter — `errors` may be a page of it. */
  total: number;
  /** Per-bucket totals, so the filter can name its own sizes. */
  counts?: { open: number; muted: number; resolved: number };
  reporting?: OpsErrorReporting;
}

/**
 * One 5xx row of `api_logs`.
 *
 * There is no `licenseKey` here and there must never be one — see
 * `getOpsServerErrors`. `errorMessage` is redacted server-side.
 */
export interface OpsServerError {
  id: number;
  /** Path as stored: relative to the mount that logged it. */
  endpoint: string;
  method: string;
  statusCode: number;
  durationMs: number | null;
  /** Epoch ms. */
  timestamp: number;
  errorMessage: string | null;
}

export interface OpsServerErrorsResponse {
  success: boolean;
  windowDays: number;
  total: number;
  entries: OpsServerError[];
  /** e.g. `/api/admin/*` — what this table can see at all. */
  scope?: string;
}

export interface OpsMailFailure {
  id: number;
  recipient: string;
  emailType: string;
  /** bounced | complained | failed | suppressed, or null for an SMTP-era row. */
  deliveryState: string | null;
  /** Verbatim provider reason. */
  deliveryDetail: string | null;
  /** Epoch ms — `failed_at` when there is a verdict, else `created_at`. */
  at: number;
}

export interface OpsMailHealth {
  success: boolean;
  windowDays: number;
  /** Rows in the window. `delivered + failed + delayed + noVerdict` = this. */
  total: number;
  delivered: number;
  /** bounced + complained + failed + suppressed — four states, one meaning. */
  failed: number;
  delayed: number;
  /** No provider verdict at all. Not a delivery. */
  noVerdict: number;
  /** `email_logs.status = 'failed'` — the send call itself threw. */
  sendFailures: number;
  /**
   * Epoch ms of the oldest row carrying any verdict — the Resend cutover.
   * `null` while no verdict has ever been recorded, which the page must say
   * out loud rather than paint 100% "no verdict" as a delivery problem.
   *
   * All-time, not windowed: once one verdict exists this can never become
   * null again, so it explains the backlog and can never be a warning.
   */
  verdictSince: number | null;
  /**
   * Epoch ms of the newest verdict the provider sent, all-time — the "is the
   * webhook still alive" stamp. Every number on this card is frozen at this
   * moment; if it is old, the delivery figures describe a period that ended
   * then, exactly like the telemetry page's newest-ping date.
   */
  newestVerdictAt: number | null;
  /** Epoch ms of the newest mail LOGGED in the window. Past `newestVerdictAt` = mail went out that nothing answered for. */
  newestLoggedAt: number | null;
  recentFailures: OpsMailFailure[];
}

/**
 * `unmeasured` is the wave-4b addition: a check that has no data source at all
 * (Mcp Tools counts a metrics map with zero writers) is not healthy, and
 * counting it as such is how "8 of 8 healthy" was reported by a page where one
 * check could not fail.
 */
export type OpsHealthCheckStatus =
  | 'healthy'
  | 'warning'
  | 'critical'
  | 'unhealthy'
  | 'unmeasured'
  | 'unknown';

export interface OpsHealthCheck {
  status: OpsHealthCheckStatus;
  message?: string;
  /** ms. */
  responseTime?: number;
  details?: Record<string, unknown>;
  /** Why this check reports nothing. Rendered verbatim beside the tile. */
  note?: string;
}

export interface OpsHealthSummary {
  total: number;
  healthy: number;
  warnings: number;
  critical: number;
  /** Checks that measure nothing. Never part of `healthy`. */
  unmeasured?: number;
  /** `total - unmeasured` — the denominator of "N of M healthy". */
  measured?: number;
}

export interface OpsHealth {
  /** healthy | degraded | unhealthy. */
  status: string;
  /** Epoch ms. */
  timestamp: number;
  /** Seconds. */
  uptime: number;
  /**
   * The running build. Read from `package.json`, not from
   * `npm_package_version` — that variable is set by `npm run`, and production
   * starts with `node dist/index.js`, so the tile you check a deploy with sat
   * on its `1.0.0` fallback forever.
   */
  version: string;
  checks: Record<string, OpsHealthCheck>;
  summary?: OpsHealthSummary;
  totalCheckTime?: number;
}

// ===== REPORTING — ACTIVATION FUNNEL =====
//
// Every type below mirrors, field for field, what
// `LicenseDatabase.getActivationFunnel()` returns in
// portal/backend/src/database/schema.ts. The route adds nothing but parameter
// validation and spreads that object into the response, so there is exactly
// one vocabulary between the SQL, the wire and the screen — the keys here are
// the keys the i18n labels are filed under, and the bucket keys are the same
// `stage` values `buildAccountFilter` resolves.

/**
 * How a signup got its account, derived from the OWNER's credentials
 * (`SIGNUP_SOURCE_SQL`). There is no `signup_source` column; `other` is not a
 * rounding error but the two production organisations that carry neither a
 * password hash nor an OAuth id.
 */
export type ActivationSignupSourceKey = 'password' | 'google' | 'github' | 'other';

/**
 * Everything is scoped to ORGANISATIONS created inside the month range, so a
 * finding is a link. Unknown values mean "no filter" on both ends — a stale
 * link widens the selection, it never empties it.
 *
 * `internal` and `sub` deliberately reuse the /admin/accounts vocabulary
 * (`AccountInternalFilter`, `AccountSubFilter`): every bucket on this page
 * deep-links into that list, and a second spelling of "trialing" would put two
 * numbers under one word again.
 */
export interface ActivationFunnelFilters {
  /** Signup month, inclusive, `YYYY-MM`. */
  from?: string;
  /** Signup month, inclusive, `YYYY-MM`. */
  to?: string;
  /** Default `exclude` — our own organisations are two of the largest. */
  internal?: AccountInternalFilter;
  source?: ActivationSignupSourceKey;
  sub?: AccountSubFilter;
}

/**
 * The eleven steps, in order. Every one of them is a count over a LIVING table
 * — no step depends on an event that has to be emitted first.
 *
 * `logged_in` is `last_login_at IS NOT NULL` (52 organisations), not
 * `login_count > 0` (40): verifying an email address opens a session and
 * stamps the timestamp without touching the counter.
 *
 * `paid` counts payments over EUR 1,00: Mollie mandate checks are stored as
 * paid EUR 0,01 rows, and without that floor the funnel reports four paying
 * organisations where there are none.
 */
export type ActivationStepKey =
  | 'signed_up'
  | 'email_verified'
  | 'logged_in'
  | 'returned'
  | 'instance_connected'
  | 'instance_tested'
  | 'llm_key'
  | 'chat_opened'
  | 'first_message'
  | 'tool_run'
  | 'paid';

export interface ActivationFunnelStep {
  key: ActivationStepKey;
  count: number;
  /**
   * The PRECEDING step's count — `null` on the first step, and legitimately
   * SMALLER than `count`, because this funnel is not monotonic: organisations
   * open a chat without ever adding an LLM key. That is why the page draws
   * horizontal bars and not a tapering funnel shape.
   */
  previousCount: number | null;
  /** Signups in this selection — the denominator of "% of all signups". */
  signedUp: number;
}

/**
 * "Furthest step reached", EXCLUSIVE: every organisation is counted once and
 * the seven buckets sum to the signup step. This is the block that carries the
 * decision — the bars cannot show it, because in a non-monotonic funnel a bar
 * says how many passed a gate, never how many are stuck at it.
 *
 * `connected_no_chat` is wider than its label: the ladder step is "has an
 * instance OR a key but never opened a chat", so an organisation that added a
 * key without an instance is not counted as having connected nothing.
 */
export type ActivationBucketKey =
  | 'never_verified'
  | 'verified_never_logged_in'
  | 'logged_in_nothing_connected'
  | 'connected_no_chat'
  | 'chat_no_message'
  | 'past_first_message'
  | 'tool_run';

export interface ActivationFurthestBucket {
  key: ActivationBucketKey;
  count: number;
  /**
   * Mean whole days since the last login, or since signup for an organisation
   * that never logged in. `null` when the bucket is empty.
   */
  avgDaysSilent: number | null;
  /**
   * Query string for /admin/accounts WITHOUT the leading `?`, or `null` when
   * that list cannot reproduce this bucket EXACTLY — the tile then renders as
   * plain text. Six of the seven are null today: the list takes one
   * single-condition filter at a time and `filter=activated` is the only one
   * that means the same thing as a bucket.
   */
  accountsFilter: string | null;
}

export type ActivationTimeToValueKey = 'signup_to_instance' | 'signup_to_llm_key';

/**
 * Minutes from organisation signup to the first instance / first LLM key, one
 * value per organisation that got there, ascending.
 *
 * Raw values, not a histogram: at n=10 and n=4 a histogram invents a shape the
 * data does not have. Both series are survivorship-biased by construction —
 * they measure only the organisations that finished the step — which is what
 * `neverReached` is for, and the page has to say so next to them.
 */
export interface ActivationTimeToValue {
  key: ActivationTimeToValueKey;
  /** Minutes, ascending, two decimals. Possibly a sample — see `sampled`. */
  values: number[];
  /** Organisations that reached the step. Exact, even when `values` is sampled. */
  n: number;
  /** Organisations in the selection that never reached it. */
  neverReached: number;
  /** `values` is an evenly spaced sample of `n` rather than all of it. */
  sampled: boolean;
}

/**
 * Where in the ONBOARDING WIZARD someone dropped out is not answerable yet:
 * migration 142 runs and `routes/portal-auth.ts` emits the event, but
 * `portal_user_audit_log` holds no `onboarding_completed` rows at all.
 *
 * `measuringSince` (epoch ms, read from `migrations_history` so it is right
 * per environment) is when the emitter went live and `observedEvents` is what
 * has landed since. A UI that renders `observedEvents` as a bare 0 states
 * "nobody completed onboarding" where the truth is "nothing has been measured
 * yet".
 */
export interface ActivationOnboardingMeasurement {
  observedEvents: number;
  measuringSince: number | null;
}

export interface ActivationFunnelData {
  success: boolean;
  /** The filters the backend actually applied, after validation. */
  filters: ActivationFunnelFilters;
  /** Epoch ms the numbers were computed. */
  generatedAt: number;
  /** Organisations in the selection; equal to the `signed_up` step's count. */
  signedUp: number;
  steps: ActivationFunnelStep[];
  /** Seven buckets, far → near. Sums to `signedUp`. */
  buckets: ActivationFurthestBucket[];
  timeToValue: ActivationTimeToValue[];
  onboarding: ActivationOnboardingMeasurement;
}

// ===== COCKPIT =====

/**
 * Every cockpit number carries the list it came from, as a relative path the
 * dashboard hands straight to `<Link to>`. The paths are produced server-side
 * so the number and the rows behind it can never drift apart — a tile that
 * says 15 opens the 15.
 */
interface CockpitLinked {
  /**
   * Relative path + querystring, e.g. `/admin/accounts?created=30d`.
   *
   * Absent when the destination is behind a route this session may not open —
   * a marketing user gets the error count without a link to /admin/ops
   * rather than a link that ends on the access-denied screen. The number is
   * still true; only the page listing it is not theirs.
   */
  to?: string;
}

export interface CockpitMetric extends CockpitLinked {
  value: number;
}

/**
 * Activation = the org ran at least one ServiceNow tool (≥ 1 `mcp_usage` row).
 * `outOf` is the cohort the value is a share of (orgs created in the same 30
 * days), because "0 activated" and "0 of 15 activated" are different facts.
 */
export interface CockpitActivation extends CockpitLinked {
  value: number;
  outOf: number;
}

/**
 * Revenue actually being collected, from the latest subscription row per
 * organisation with status `active`.
 *
 * MRR is the ONE tile that counts our own organisations, because Mollie
 * charges them too. `internalCents` / `internalPayingAccounts` are the share
 * of the two numbers above that comes from those accounts, and the tile MUST
 * show it as soon as `internalCents > 0` — today that is 100% of the total,
 * so a bare "€19,00 · 1 paying account" reads as "we have a customer" while
 * every word of it is true. Leaving these two fields unrendered is how that
 * sentence gets published.
 */
export interface CockpitMrr extends CockpitLinked {
  cents: number;
  payingAccounts: number;
  internalCents: number;
  internalPayingAccounts: number;
}

export interface CockpitTrials extends CockpitLinked {
  value: number;
  /** Trials ending within 7 days. */
  endingSoon: number;
  /** Trialing orgs with zero `mcp_usage` rows — trials that never started. */
  neverRanTool: number;
}

export interface CockpitTickets extends CockpitLinked {
  value: number;
  /** Age of the oldest OPEN ticket in whole days; 0 when there are none. */
  oldestDays: number;
}

/** Backend-authored severity strings (Dutch, per the cockpit contract). */
export type CockpitSignalSeverity = 'hoog' | 'midden' | 'laag';

export interface CockpitSignal extends CockpitLinked {
  /** Stable identifier, e.g. `past_due`. Drives the i18n label. */
  key: string;
  /** Always > 0 — the backend omits empty signals rather than sending zeroes. */
  count: number;
  severity: CockpitSignalSeverity;
}

export interface CockpitResponse {
  success: boolean;
  /** Epoch ms the numbers were computed. */
  generatedAt: number;
  newOrgs30d: CockpitMetric;
  activated30d: CockpitActivation;
  /**
   * `null` — not omitted — for the marketing role, so the frontend can tell
   * "you may not see this" apart from "not loaded yet" and render neither a
   * zero nor an em dash where revenue belongs.
   */
  mrr: CockpitMrr | null;
  trials: CockpitTrials;
  openTickets: CockpitTickets;
  /** Distinct unresolved error fingerprints last seen in the past 7 days. */
  openErrors7d: CockpitMetric;
  /**
   * Only rows with `count > 0`; an all-clear platform sends `[]` and the rail
   * disappears. Optional so a backend that predates the field renders no rail
   * instead of crashing the page.
   */
  signals?: CockpitSignal[];
}

// ===== ACCOUNTS =====

/**
 * Behavioural filters. One at a time; anything else means "no filter".
 *
 * `activated` / `not-activated` are the cockpit's activation definition seen
 * from the list side: an organisation is activated once it has run at least one
 * ServiceNow tool (≥ 1 `mcp_usage` row). The backend resolves both from the one
 * exported SQL condition the cockpit counts with — the two screens cannot drift
 * into two definitions of the same word the way Reporting's "Retention" and
 * "MAU · active rate" did.
 */
export type AccountFilter =
  | 'no-instance'
  | 'untested'
  | 'never-logged-in'
  | 'no-llm-key'
  | 'no-messages'
  | 'activated'
  | 'not-activated'
  /** At least one ServiceNow instance whose last connection test failed. */
  | 'instance-failed';

/**
 * Created within this window. The cockpit links `created=30d` (its "new
 * accounts (30d)" tile); the other values exist so the same control can offer a
 * shorter/longer window. Unknown values mean "no filter" on both ends.
 */
export type AccountCreatedFilter = '7d' | '30d' | '90d';
/**
 * The seven exclusive buckets of the activation funnel. A bucket links here
 * as ?stage=<key>; the backend resolves it with the same ladder that produced
 * the count, so the list holds exactly the organisations the funnel counted.
 */
export type AccountStageFilter =
  | 'never_verified'
  | 'verified_never_logged_in'
  | 'logged_in_nothing_connected'
  | 'connected_no_chat'
  | 'chat_no_message'
  | 'past_first_message'
  | 'tool_run';

export type AccountSubFilter = 'trialing' | 'active' | 'free' | 'past_due' | 'none';

/** Trial ends within this window. */
export type AccountEndingFilter = '7d' | '14d' | '30d';

/** Internal (own) organisations are excluded by default. */
export type AccountInternalFilter = 'exclude' | 'include' | 'only';

/**
 * Sortable columns. Sorting happens server-side over the whole result set —
 * sorting the current page only would reorder 50 of 58 rows and read as a
 * complete ordering. An unknown value means "backend default", never an error.
 *
 * These strings travel to the server verbatim and must stay identical to the
 * keys of `ACCOUNT_SORT_EXPRESSIONS` / `ACCOUNT_SORT_KEYS` in the backend
 * (`database/schema.ts`, `routes/admin.ts`). Both ends treat an unknown key as
 * "no opinion" rather than an error, so a mismatch does NOT surface as a 400 or
 * a type error — the arrow moves and the rows stay put. Rename on one side only
 * and sorting silently stops working.
 */
export type AccountSortField =
  | 'created'
  | 'lastLogin'
  | 'loginCount'
  | 'chats'
  | 'toolRuns'
  | 'daysSilent';

export type AccountSortDir = 'asc' | 'desc';

export interface AccountFilters {
  search?: string;
  filter?: AccountFilter | '';
  created?: AccountCreatedFilter | '';
  stage?: AccountStageFilter | '';
  sub?: AccountSubFilter | '';
  ending?: AccountEndingFilter | '';
  internal?: AccountInternalFilter;
  sort?: AccountSortField | '';
  dir?: AccountSortDir;
  /** Max 200, default 50. */
  limit?: number;
  offset?: number;
}

export interface AccountSubscription {
  status: string;
  tier: string | null;
  /** Epoch ms. */
  trialEndsAt: number | null;
  pricePerSeatCents: number | null;
  seats: number | null;
  mollieSubscriptionId: string | null;
  billingInterval: string | null;
  /**
   * What the account keeps AFTER the trial — chosen during onboarding, so it
   * is empty for every trialist who never finished that step. Read-only here:
   * the modules endpoint writes `subscription_modules` (what is active NOW),
   * not this list. Optional so a backend that has not shipped it yet renders
   * as "unknown" instead of crashing the panel.
   */
  preselectedModules?: string[];
}

/**
 * The fields `PUT /api/admin/users/:id/subscription` actually accepts. Send
 * only what changed — the endpoint 400s on an empty patch, which is the
 * correct outcome for "save" with nothing edited.
 *
 * `trialEndsAt` is deliberately NOT edited from the subscription form: the
 * trial endpoint is the one that re-syncs the Mollie start date, and moving
 * the date without that resync is how a trialing customer gets charged early.
 */
export interface AccountSubscriptionPatch {
  status?: string;
  totalAmountCents?: number;
  pricePerSeatCents?: number;
  cancelAtPeriodEnd?: boolean;
  /**
   * Confirms a deliberate 0. The endpoint refuses a zero amount without it and
   * answers 400 + `requiresConfirmation: 'allowZeroAmount'`, so without this
   * field setting a price to EUR 0 (goodwill, internal org, suspended account)
   * is simply not doable from the UI.
   */
  allowZeroAmount?: boolean;
}

export interface AccountInstanceCounts {
  total: number;
  ok: number;
  failed: number;
  untested: number;
}

export interface AccountRow {
  organizationId: number;
  organizationName: string | null;
  ownerUserId: number | null;
  ownerEmail: string | null;
  isInternal: boolean;
  /** Epoch ms. */
  createdAt: number;
  /** Unreliable on its own: 12 accounts have 0 here plus a real lastLoginAt. */
  loginCount: number;
  lastLoginAt: number | null;
  /** Whole days since lastLoginAt, or since createdAt if never logged in. */
  daysSilent: number;
  instances: AccountInstanceCounts;
  llmKeys: number;
  chatSessions: number;
  userMessages: number;
  chatErrors: number;
  toolRuns: number;
  /**
   * Active members with the platform role `marketing` — the role that opens
   * the admin panel. Optional so a backend that predates it renders no badge
   * instead of "0 marketing" it cannot vouch for.
   */
  marketingMembers?: number;
  subscription: AccountSubscription | null;
}

/**
 * What this SELECTION (all filtered rows, not just the visible page) wants to
 * keep after the trial. `base` is left out — every account has it.
 */
export interface AccountModuleSummary {
  top: { moduleId: string; accounts: number }[];
  /** Trialing accounts that never picked anything. */
  trialNotChosen: number;
}

export interface AccountsResponse {
  success: boolean;
  total: number;
  accounts: AccountRow[];
  /** Absent on a backend that predates the aggregate; the bar hides then. */
  moduleSummary?: AccountModuleSummary;
}

export interface AccountMember {
  /** null for a pending invite with no portal_users row yet — not editable. */
  userId: number | null;
  email: string | null;
  /** Membership role in THIS organisation: owner/admin/developer/stakeholder. */
  role: string;
  status: string;
  lastLoginAt: number | null;
  /** Display name on portal_users. */
  name?: string | null;
  /**
   * PLATFORM role (`portal_users.role`), not the membership role above:
   * 'marketing' is what opens this admin panel at all. Every field below is
   * optional so a payload without it degrades to "unknown" rather than
   * showing a confident wrong value.
   */
  portalRole?: 'user' | 'marketing' | string;
  emailVerified?: boolean;
  onboardingCompleted?: boolean;
  /** Epoch ms. */
  createdAt?: number;
  loginCount?: number;
}

export interface AccountInstanceEntry {
  id: number;
  instanceUrl: string;
  /** 'success' | 'failed' | 'not_tested'. */
  lastTestStatus: string | null;
  lastTestedAt: number | null;
}

export interface AccountChatError {
  createdAt: number;
  /** Token-redacted, max 200 chars. */
  snippet: string;
}

export interface AccountDetail extends AccountRow {
  members: AccountMember[];
  activeFeatures: string[];
  instanceList: AccountInstanceEntry[];
  recentChatErrors: AccountChatError[];
}

export type AiCrawlerCategory = 'training' | 'search' | 'assistant' | 'seo' | 'other';

export interface AiCrawlerStats {
  period: string;
  cloudflare: {
    configured: boolean;
    hosts: string[];
    lastSyncedAt: number | null;
    latestDate: string | null;
  };
  summary: {
    totalRequests: number;
    distinctBots: number;
    distinctOperators: number;
    topBot: string | null;
    daysWithData: number;
  };
  daily: { date: string; requests: number }[];
  byBot: { botName: string; operator: string; category: AiCrawlerCategory; requests: number; lastSeen: string }[];
  byHost: { hostname: string; requests: number }[];
  byCategory: { category: AiCrawlerCategory; requests: number }[];
  byPage: { path: string; hostname: string; requests: number; topBot: string | null }[];
}

export interface AiCrawlerSyncResult {
  skipped: boolean;
  reason?: string;
  daysSynced: number;
  rowsUpserted: number;
  pageRowsUpserted: number;
  totalBotRequests: number;
}

export interface AnonymousTelemetryStats {
  uniqueMachines: number;
  totalSessions: number;
  avgSessionDurationSec: number;
  avgMessageCount: number;
  topVersions: { version: string; count: number }[];
  osDistribution: { os: string; count: number }[];
  dailyActive: { date: string; uniqueMachines: number; sessions: number }[];
  archDistribution: { arch: string; count: number }[];
  channelDistribution: { channel: string; count: number }[];
  durationDistribution: { bucket: string; count: number }[];
  /**
   * `measurable` is false when the archive predates the oldest surviving raw
   * ping: compaction deletes raw rows, first_seen walks forward with it, and
   * everyone reads as new. Printing `returningMachines` without checking this
   * publishes "0% retention" as churn when it measures the retention job.
   */
  retention: { newMachines: number; returningMachines: number; firstSeenFloor: number | null; measurable: boolean };
  versionAdoption: { date: string; version: string; machines: number }[];
  engagementDistribution: { bucket: string; machineCount: number }[];
  countryDistribution?: { country: string; count: number }[];
  recentPings?: { machineId: string; version: string; channel: string; os: string; arch: string; type: string; country: string | null; timestamp: number }[];
  machineBreakdown?: { machineId: string; versions: string; channels: string; os: string; arch: string; country: string | null; firstSeen: number; lastSeen: number; totalPings: number }[];
  /**
   * Each percentage with the previous-period value it was computed against. A
   * base of 0 makes the backend emit a flat +100% (division by zero), and a
   * base of 1 makes a real division meaningless — neither is visible from the
   * percentage, so the base has to be read before the trend is rendered.
   */
  growthTrends?: {
    machinesChangePercent: number;
    sessionsChangePercent: number;
    avgDurationChangePercent: number;
    avgMessagesChangePercent: number;
    previousMachines: number;
    previousSessions: number;
    previousAvgDurationSec: number;
    previousAvgMessages: number;
  };
  sessionCompletionRate?: { starts: number; ends: number; completionPercent: number };
  dailyCompletionRate?: { date: string; starts: number; ends: number; percent: number }[];
  dailyEngagement?: { date: string; avgDuration: number; avgMessages: number }[];
  usageHeatmap?: { dayOfWeek: number; hourUtc: number; sessions: number }[];
  cohortRetention?: { cohortWeek: string; cohortSize: number; retention: number[] }[];
  countryGrowth?: { country: string; currentMachines: number; previousMachines: number; changePercent: number }[];
  /** `currentSharePercent` is measured on `shareReferenceDate` — ONE day for every version, which is what stops three versions each claiming 100%. */
  versionVelocity?: { version: string; releasedDate: string; daysToReach50Percent: number | null; currentSharePercent: number; shareReferenceDate: string | null }[];
  exitReasonDistribution?: { exitReason: string; count: number }[];
  installMethodDistribution?: { installMethod: string; count: number }[];
  stickiness?: { dau: number; mau: number; ratio: number };
  /**
   * Machine-DAYS in the archive, not machines: one machine active on forty
   * archived days counts forty times. Never add this to `uniqueMachines` —
   * doing exactly that is what produced "187 unique machines" out of 9.
   */
  archivedMachineDays?: number;
  dataInfo?: { rawPingsCount: number; summaryDaysCount: number; oldestRawDate: string | null; oldestSummaryDate: string | null };
  toolMix?: { builtin: number; servicenow: number; mcp: number; machinesUsingServicenow: number; machinesReporting: number };
  providerDistribution?: { provider: string; count: number }[];
  activationFunnel?: { installed: number; engaged: number; servicenowActivated: number; retained: number };
}

export interface ErrorTelemetrySessions {
  sessions: Array<{
    version: string;
    os: string;
    arch: string;
    sessionDurationSec: number;
    messageCount: number;
    timestamp: number;
    exitErrorMessage: string | null;
    country: string | null;
    installMethod: string | null;
  }>;
  errorGroups: Array<{ message: string; count: number; latestTimestamp: number }>;
}

export interface EmailLogItem {
  id: number;
  recipient: string;
  subject: string;
  emailType: string;
  status: 'sent' | 'failed';
  provider?: string;
  messageId?: string;
  errorMessage?: string;
  hasHtmlBody?: boolean;
  htmlBody?: string;
  metadata?: Record<string, any>;
  createdAt: number;
}

export interface EmailLogsResponse {
  success: boolean;
  logs: EmailLogItem[];
  total: number;
  stats: {
    sent: number;
    failed: number;
    byType: Record<string, number>;
  };
}

// ===== REVENUE =====
//
// Wave 5. Three pages became one: /admin/billing (six `parseFloat` calls on
// pre-formatted strings, printing "€ NaN" over three dropped tables),
// /admin/invoices + /admin/invoices/:id (sixteen calls, all 404, all swallowed)
// and /admin/upgrade-requests (working since wave 0).
//
// ONE endpoint serves the page: `GET /api/admin/revenue`. The types below
// mirror `RevenueOverview` in backend/src/database/schema.ts field for field,
// because the previous split into three urls was how the whole page died in
// the first place — the frontend described a surface the backend never
// mounted, and every call 404'd into a swallowed catch. If a field is not in
// this file, the backend does not select it; do not add it here first.
//
// Every amount below is CENTS, integer, always. No amount in this module is a
// formatted string: the €NaN on /admin/billing was exactly that — the backend
// sent "€19.00" and the frontend ran `parseFloat` over it.

/** The six values of `subscriptions.status`. */
export type RevenueSubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'unpaid'
  | 'free'
  | 'canceled';

/** `payments.status` and `payments.provider`, verbatim. */
export type RevenuePaymentStatus =
  | 'pending'
  | 'paid'
  | 'failed'
  | 'expired'
  | 'refunded'
  | 'chargeback';

export type RevenuePaymentProvider = 'mollie' | 'x402';

/** `invoices.status` — the real table's enum, not the retired eight-value one. */
export type RevenueInvoiceStatus = 'draft' | 'open' | 'paid' | 'void';

/**
 * One row of the subscription book. EVERY row is returned, not one per
 * organisation: organisation 56 carries a `canceled` row and the `active` row
 * that replaced it, and showing only the winner hides that a cancellation
 * happened.
 */
export interface RevenueSubscription {
  subscriptionId: number;
  organizationId: number;
  organizationName: string | null;
  ownerEmail: string | null;
  /** `organizations.is_internal` (migration 147) — our own account. */
  isInternal: boolean;
  status: RevenueSubscriptionStatus;
  tier: string | null;
  pricePerSeatCents: number;
  developerSeats: number;
  stakeholderSeats: number;
  /** developer + stakeholder — what is actually billed. */
  seats: number;
  billingInterval: 'monthly' | 'yearly' | null;
  currency: string;
  /** Epoch ms. Still filled on rows that already fell back to `free`. */
  trialEndsAt: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: number | null;
  mollieSubscriptionId: string | null;
  createdAt: number;
  /** `MRR_CENTS_SQL` for this row. Meaningless unless `countsTowardMrr`. */
  mrrCents: number;
  /** False for superseded rows, which stay listed rather than disappearing. */
  isCurrentForOrg: boolean;
  /**
   * This row is part of the MRR total: `status = 'active'` AND it is the
   * latest subscription of its organisation. Organisation 56 has two rows —
   * one `canceled`, one `active` — and only the second one counts.
   */
  countsTowardMrr: boolean;
}

export interface RevenueStatusCount {
  status: RevenueSubscriptionStatus;
  /** Organisations, not rows: counted over the current row per organisation. */
  organizations: number;
  internalOrganizations: number;
}

/**
 * The money totals, computed server-side with `MRR_CENTS_SQL` over the latest
 * `active` subscription per organisation — byte-identical to the cockpit tile
 * and the Reporting summary, because three screens showing three totals for
 * one word is what this wave exists to remove.
 */
export interface RevenueTotals {
  mrrCents: number;
  /** The share of `mrrCents` that comes from our own organisations. */
  internalMrrCents: number;
  /** mrrCents − internalMrrCents. Today this is 0, and the page says so. */
  externalMrrCents: number;
  /** `payingOrganizationSql` — above the €0.01 mandate check, not `status = 'paid'`. */
  payingOrganizations: number;
  organizationsWithSubscription: number;
  /** Rows in `subscriptions`, which exceeds the organisation count. */
  subscriptionRows: number;
  /** Rows a newer row for the same organisation replaced. */
  supersededRows: number;
  byStatus: RevenueStatusCount[];
}

export interface RevenueInvoice {
  id: number;
  invoiceNumber: string;
  organizationId: number;
  organizationName: string | null;
  isInternal: boolean;
  billingName: string | null;
  billingEmail: string | null;
  status: RevenueInvoiceStatus;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  currency: string;
  subscriptionId: number | null;
  /** `invoices.payment_id`. NULL on the one production invoice. */
  paymentId: number | null;
  /** Epoch ms. */
  invoiceDate: number;
  dueDate: number | null;
  paidAt: number | null;
  pdfUrl: string | null;
}

export interface RevenueInvoiceTotals {
  /** Rows in `invoices`. */
  total: number;
  /** Rows actually returned — below `total` only if the cap bound. */
  returned: number;
  paid: number;
  open: number;
  draft: number;
  void: number;
  paidCents: number;
  openCents: number;
}

export interface RevenuePayment {
  id: number;
  organizationId: number;
  organizationName: string | null;
  isInternal: boolean;
  provider: RevenuePaymentProvider;
  amountCents: number;
  currency: string;
  status: RevenuePaymentStatus;
  paymentMethod: string | null;
  molliePaymentId: string | null;
  description: string | null;
  failureReason: string | null;
  createdAt: number;
  paidAt: number | null;
  /**
   * A €0.01 trial-mandate check, not revenue. Decided by the backend with the
   * same constant `payingOrganizationSql()` uses, so this page and the
   * activation funnel cannot end up with two definitions of "has paid".
   */
  isMandateCheck: boolean;
}

export interface RevenuePaymentTotals {
  total: number;
  returned: number;
  mandateChecks: number;
  mandateChecksPaid: number;
  paid: number;
  failed: number;
  expired: number;
  pending: number;
  /** refunded + chargeback. */
  reversed: number;
  /**
   * Paid, above the mandate threshold. This is what the PORTAL has recorded,
   * and it is not what Mollie has collected — recurring charges after the
   * first never made it through the webhook, so this number is a floor.
   */
  collectedCents: number;
  lastCollectedAt: number | null;
}

/**
 * THE PORTAL SIDE of one Mollie subscription — and nothing else.
 *
 * Portal and Mollie are known to disagree on all three live mandates, but the
 * disagreement cannot be proven from this database: the other half needs one
 * Mollie API call per subscription, and `services/mollie-service.ts` has no
 * read method for a Mollie-side subscription. So every field here is a fact
 * the portal owns, and none of it may be rendered as "Mollie says".
 */
export interface RevenueReconciliationRow {
  subscriptionId: number;
  organizationId: number;
  organizationName: string | null;
  isInternal: boolean;
  mollieSubscriptionId: string;
  mollieMandateId: string | null;
  portalStatus: RevenueSubscriptionStatus;
  /** price_per_seat_cents × seats — what the portal thinks is billed per interval. */
  portalAmountCents: number;
  billingInterval: 'monthly' | 'yearly' | null;
  currency: string;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: number | null;
  lastPayment: RevenueLastPayment | null;
  /** `paid_at` of the last payment above the mandate threshold. */
  lastChargedAt: number | null;
  /** Sum of that organisation's paid, above-threshold payments. */
  collectedCents: number;
  /** A live Mollie id with no real charge behind it — all of today's discrepancies. */
  neverCharged: boolean;
  daysSinceLastCharge: number | null;
}

/** The last payment recorded against a reconciled subscription's organisation. */
export interface RevenueLastPayment {
  id: number;
  amountCents: number;
  status: RevenuePaymentStatus;
  createdAt: number;
  paidAt: number | null;
  isMandateCheck: boolean;
}

/** The single row of `platform_settings`. */
export interface PlatformSettings {
  id: number;
  companyName: string;
  companyAddress: string;
  companyKvk: string;
  companyVat: string;
  companyIban: string;
  companyEmail: string;
  companyWebsite: string;
  /** Epoch ms, `0` when nobody has ever edited the row. */
  updatedAt: number;
}

/** Which issuer fields still hold migration 137's seed value. */
export type PlatformSettingsPlaceholderField = 'companyKvk' | 'companyVat' | 'companyIban';

/** GET and PUT /api/admin/platform-settings both answer with this. */
export interface PlatformSettingsWithFlags extends PlatformSettings {
  placeholderFields: PlatformSettingsPlaceholderField[];
}

export type PlatformSettingsPatch = Partial<Omit<PlatformSettings, 'id' | 'updatedAt'>>;

/** Everything `GET /api/admin/revenue` answers with. */
export interface RevenueOverviewResponse {
  success: boolean;
  /** Epoch ms the numbers were computed. */
  generatedAt: number;
  subscriptions: RevenueSubscription[];
  totals: RevenueTotals;
  invoices: RevenueInvoice[];
  invoiceTotals: RevenueInvoiceTotals;
  payments: RevenuePayment[];
  paymentTotals: RevenuePaymentTotals;
  reconciliation: RevenueReconciliationRow[];
  /** Issuer identity printed on every generated invoice PDF. */
  company: PlatformSettings;
  /** Which of those fields are still the migration-137 placeholder. */
  companyPlaceholderFields: PlatformSettingsPlaceholderField[];
}

export type RevenueUpgradeRequestType =
  | 'customer_developer_seats'
  | 'customer_stakeholder_seats'
  | 'si_developer_seats'
  | 'si_stakeholder_seats'
  | 'si_customer_slots'
  | 'org_enterprise_plan';

export type RevenueUpgradeRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export type RevenueUpgradeRequestUrgency = 'low' | 'medium' | 'high';

/**
 * One row of `upgrade_requests`, as `GET /api/upgrade-requests/admin` returns
 * it — snake_case, because that route answers with the database row. The
 * `customer_*` / `si_*` name fields came from two tables migration 114
 * dropped and wave 0 removed those joins, so they arrive undefined on every
 * row and exist here only so an old row still renders a name-shaped blank
 * instead of a type error.
 */
export interface RevenueUpgradeRequest {
  id: number;
  request_type: RevenueUpgradeRequestType;
  organization_id: number | null;
  current_amount: number;
  requested_amount: number;
  additional_amount: number;
  reason: string | null;
  urgency: RevenueUpgradeRequestUrgency;
  status: RevenueUpgradeRequestStatus;
  reviewed_by: string | null;
  reviewed_at: number | null;
  review_notes: string | null;
  price_impact_cents: number | null;
  invoice_updated: boolean;
  created_at: number;
  updated_at: number;
  org_name?: string;
  org_email?: string;
  customer_name?: string;
  customer_email?: string;
  si_name?: string;
  si_email?: string;
}

export interface RevenueUpgradeRequestStats {
  pending: number;
  approved: number;
  rejected: number;
  approvedThisMonth: number;
}

